// D&D 5e Currency Conversion Utilities
// 1gp = 10sp = 100cp
// 1pp = 10gp

export const formatCurrency = (valueInGold) => {
  // [FIX JAN 2026]: Handle undefined/null/NaN values to prevent "NaNcp" display
  // [WHY]: Items without value property were showing "NaNcp" in inventory table
  if (valueInGold === undefined || valueInGold === null || isNaN(valueInGold) || valueInGold === 0) return '0cp';
  
  // Handle values >= 1gp normally
  if (valueInGold >= 1) {
    // Round to 2 decimal places for gold
    const rounded = Math.round(valueInGold * 100) / 100;
    if (rounded === Math.floor(rounded)) {
      return `${Math.floor(rounded)}gp`;
    }
    return `${rounded}gp`;
  }
  
  // Convert sub-gold values to smaller denominations
  if (valueInGold >= 0.1) {
    // Silver range (0.1gp - 0.9gp)
    const silver = Math.round(valueInGold * 10);
    return `${silver}sp`;
  }
  
  // Copper range (less than 0.1gp)
  const copper = Math.round(valueInGold * 100);
  return `${copper}cp`;
};

export const formatCurrencyLong = (valueInGold) => {
  if (valueInGold === 0) return '0 copper pieces';
  
  const pp = Math.floor(valueInGold / 10);
  const gp = Math.floor(valueInGold % 10);
  const sp = Math.floor((valueInGold * 10) % 10);
  const cp = Math.round((valueInGold * 100) % 10);
  
  const parts = [];
  if (pp > 0) parts.push(`${pp} platinum`);
  if (gp > 0) parts.push(`${gp} gold`);
  if (sp > 0) parts.push(`${sp} silver`);
  if (cp > 0) parts.push(`${cp} copper`);
  
  return parts.join(', ');
};

export const parseCustomValue = (input) => {
  // Parse user input like "5gp", "10sp", "100cp", "1pp"
  const match = input.match(/^(\d+(?:\.\d+)?)\s*(pp|gp|sp|cp)?$/i);
  if (!match) return null;

  const value = parseFloat(match[1]);
  const currency = (match[2] || 'gp').toLowerCase();

  switch (currency) {
    case 'pp': return value * 10;
    case 'gp': return value;
    case 'sp': return value / 10;
    case 'cp': return value / 100;
    default: return value;
  }
};

// [NEW 2025-10-21]: Currency auto-conversion for purchases
// [FIX NOV 26, 2025]: Added electrum (EP) support - was completely missing!
// [FIX JAN 12, 2026]: Added null safety guard - Bastion tab crashed when currency undefined
// Convert all currency to copper for calculation
// D&D 5e rates: 1pp = 1000cp, 1gp = 100cp, 1ep = 50cp, 1sp = 10cp
export const getTotalCopper = (currency) => {
  // [GUARD]: Prevent crash when currency object is undefined/null
  if (!currency || typeof currency !== 'object') return 0;

  return (currency.pp || 0) * 1000 +
         (currency.gp || 0) * 100 +
         (currency.ep || 0) * 50 +    // [FIX]: EP was missing!
         (currency.sp || 0) * 10 +
         (currency.cp || 0);
};

// Convert a price in any denomination to copper
// [FIX NOV 26, 2025]: Added EP support
export const priceToCopper = (amount, denomination = 'gp') => {
  switch (denomination) {
    case 'pp': return amount * 1000;
    case 'gp': return amount * 100;
    case 'ep': return amount * 50;   // [FIX]: EP was missing!
    case 'sp': return amount * 10;
    case 'cp': return amount;
    default: return amount * 100; // Default to gold
  }
};

// Make change after a purchase
export const makeChange = (costInCopper, currentCurrency) => {
  const totalCopper = getTotalCopper(currentCurrency);

  if (totalCopper < costInCopper) {
    return null; // Not enough money
  }

  const remainingCopper = totalCopper - costInCopper;

  // Convert back to optimal denominations
  const newCurrency = {
    pp: Math.floor(remainingCopper / 1000),
    gp: Math.floor((remainingCopper % 1000) / 100),
    sp: Math.floor((remainingCopper % 100) / 10),
    cp: remainingCopper % 10
  };

  return newCurrency;
};

// [FIX NOV 26, 2025]: Complete rewrite to PRESERVE user's denomination distribution
// [WHY]: Previous version converted ALL currency to "optimal" denominations after purchase,
//        causing user's gold to "disappear" into platinum. Now spends from lowest first.
// [FIX NOV 28, 2025]: Added spendHighFirst option for bastion purchases (spend platinum first)
// [INTEGRATION]: Used by BastionPanel, shop purchases, and any currency spending
export const autoPurchase = (cost, currentCurrency, options = {}) => {
  const { spendHighFirst = false } = options; // [FIX NOV 28, 2025]: Option to spend high denom first

  // If cost is a number, assume it's in gold pieces
  const costInCopper = typeof cost === 'number'
    ? cost * 100
    : priceToCopper(cost.amount, cost.denomination);

  const totalCopper = getTotalCopper(currentCurrency);

  if (totalCopper < costInCopper) {
    return {
      success: false,
      message: `Not enough money. Need ${formatCurrency(costInCopper / 100)}, have ${formatCurrency(totalCopper / 100)}`,
      newCurrency: currentCurrency
    };
  }

  // [NEW]: Spend from lowest denomination first, preserving user's currency distribution
  const currency = { ...currentCurrency };
  let remaining = costInCopper;

  // Order: cp → sp → ep → gp → pp (spend lowest value first)
  // [FIX NOV 28, 2025]: Or reverse for spendHighFirst (pp → gp → ep → sp → cp)
  const denomOrder = [
    { key: 'cp', value: 1 },
    { key: 'sp', value: 10 },
    { key: 'ep', value: 50 },
    { key: 'gp', value: 100 },
    { key: 'pp', value: 1000 }
  ];

  // Reverse order if spending high first (for bastion purchases)
  const spendOrder = spendHighFirst ? [...denomOrder].reverse() : denomOrder;

  for (const { key, value } of spendOrder) {
    if (remaining <= 0) break;

    const available = (currency[key] || 0) * value;
    if (available <= 0) continue;

    const spendCopper = Math.min(remaining, available);
    const coinsToSpend = Math.floor(spendCopper / value);

    if (coinsToSpend > 0) {
      currency[key] = (currency[key] || 0) - coinsToSpend;
      remaining -= coinsToSpend * value;
    }
  }

  // If we still have remaining (due to denomination gaps), break larger coins
  // Example: Need 5cp but only have 1sp - spend the 1sp, get 5cp change
  if (remaining > 0) {
    for (let i = denomOrder.length - 1; i >= 0; i--) {
      const { key, value } = denomOrder[i];
      if ((currency[key] || 0) > 0 && value > remaining) {
        // Spend one of this coin and make change
        currency[key] -= 1;
        let change = value - remaining;
        remaining = 0;

        // Give change back in smaller denominations
        for (let j = i - 1; j >= 0; j--) {
          const changeKey = denomOrder[j].key;
          const changeValue = denomOrder[j].value;
          const changeCoins = Math.floor(change / changeValue);
          if (changeCoins > 0) {
            currency[changeKey] = (currency[changeKey] || 0) + changeCoins;
            change -= changeCoins * changeValue;
          }
        }
        break;
      }
    }
  }

  return {
    success: true,
    message: `Purchased for ${formatCurrency(costInCopper / 100)}`,
    newCurrency: currency,
    spent: costInCopper / 100 // Return amount spent in gold for logging
  };
};

// Convert between denominations manually (for currency exchange)
export const convertCurrency = (amount, fromDenom, toDenom) => {
  const copperValue = priceToCopper(amount, fromDenom);

  switch (toDenom) {
    case 'pp': return copperValue / 1000;
    case 'gp': return copperValue / 100;
    case 'sp': return copperValue / 10;
    case 'cp': return copperValue;
    default: return copperValue / 100;
  }
};